import { useState, useCallback, useEffect, useRef } from "react";
import {
  getPaginatedPatients,
  Patient,
  PaginatedPatientsParams,
  Diagnosis,
  Clinician,
  getDiagnoses,
  getClinicians,
} from "../../services/getFunctions";
import { useAlertHelpers } from "./useAlertHelpers";
import { toErrorType } from "../alertBoxes";

interface PaginationState {
  currentPage: number;
  totalCount: number;
  totalPages: number;
  pageSize: number;
}

interface UsePaginatedPatientsReturn {
  //static reference data
  diagnoses: Diagnosis[];
  clinicians: Clinician[]; //only for admin

  //dynamic data
  patients: Patient[];
  pagination: PaginationState;

  //loading functions
  loadPatients: (
    page: number,
    limit: number,
    params?: Partial<PaginatedPatientsParams>,
  ) => Promise<void>;
  refreshCurrentPage;

  //loading state
  isLoading: boolean;
}

/**
 * Shared Hook for loading paginated patient data, clinician data and diagnoses
 * Used by both HomeClinician and HomeClinicAdmin
 *>
 * @param pageSize - Number of items per page (default: 10)
 */
export const useHomeData = (): UsePaginatedPatientsReturn => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([]);
  const [clinicians, setClinicians] = useState<Clinician[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
    pageSize: 100, //set initial value to be 100
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  //add ref to update the pagination value
  const paginationRef = useRef(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const {
    reportGetDiagnosesListError,
    reportGetClinicianListError,
    reportGetPatientListError,
  } = useAlertHelpers();

  /**
   * load patients from backend with pagination
   *
   * @param page - Page number to load (1-indexed)
   * @param params - Optional additional parameters (order, sorting, etc.)
   */
  const loadPatients = useCallback(
    async (
      page: number = 1,
      limit: number,
      params?: Partial<PaginatedPatientsParams>,
    ) => {
      setIsLoading(true);
      try {
        const offset = page - 1;

        const paginatedParams: PaginatedPatientsParams = {
          limit: limit,
          offset: offset,
          ...params,
        };

        const result = await getPaginatedPatients(paginatedParams);

        setPatients(result.patient_information_list || []);
        setPagination({
          currentPage: page,
          totalCount: result.total_elements,
          totalPages: result.total_pages || 0,
          pageSize: limit,
        });
      } catch (error) {
        reportGetPatientListError(toErrorType(error));
      } finally {
        setIsLoading(false);
      }
    },
    [reportGetPatientListError],
  );
  /**
   * load clinicians from backend as static reference data
   */
  const loadClinicians = useCallback(async () => {
    try {
      const clinicianList = await getClinicians();
      setClinicians(clinicianList.clinicians || []);
    } catch (error) {
      console.error("[useHomeData] Error loading clinicians:", error);
      reportGetClinicianListError(toErrorType(error));
    }
  }, [reportGetClinicianListError]);

  /**
   * refresh the current page
   * called after CRUD operations
   */
  const refreshCurrentPage = useCallback(async () => {
    const current = paginationRef.current;
    await loadPatients(current.currentPage, current.pageSize);
    await loadClinicians();
  }, [loadClinicians, loadPatients]);

  /**load diagnoses from backend as static reference data
   */
  const loadDiagnoses = useCallback(async () => {
    try {
      const diagnoses = await getDiagnoses();
      setDiagnoses(diagnoses.diagnoses || []);
    } catch (error) {
      reportGetDiagnosesListError(toErrorType(error));
    }
  }, [reportGetDiagnosesListError]);

  /**
   * Auto-load static reference data on mount
   */
  useEffect(() => {
    const loadStaticData = async () => {
      await Promise.all([loadDiagnoses(), loadClinicians()]);
    };

    loadStaticData();
  }, [loadClinicians, loadDiagnoses]);

  return {
    patients,
    pagination,
    diagnoses,
    clinicians,
    loadPatients,
    refreshCurrentPage,
    isLoading,
  };
};
